import React, { useCallback } from 'react';
import { FormApi } from 'final-form';

import { LocationFragment, MembershipFragment, MembershipUpdate } from '@occc/graphql';
import {
  useUpdateMembership,
  useMembershipHomeStoresCreateOrUpdateMutation,
} from '@occc-app/dataAccessLayer/membership/mutations';

import { formatPhoneNumberToE164 } from '@occc/utils-phone-number';
import { EditModalFormFields } from './EditModalForm';
import EditModal from './EditModal';

import { DateTimeFormat, formatDateTime } from '../../../../../lib/date';

export interface IEditModalContainerProps {
  member: MembershipFragment;
  show: boolean;
  onHide: () => void;
  locations?: LocationFragment[];
}

const EditModalContainer: React.FC<IEditModalContainerProps> = ({
  member,
  show,
  onHide,
  locations,
}) => {
  const [updateMembership] = useUpdateMembership({
    refetchQueries: ['membership'],
  });
  const [createOrUpdateHomeStores] = useMembershipHomeStoresCreateOrUpdateMutation({
    refetchQueries: ['membership'],
  });

  const onSubmit = useCallback(
    async (input: EditModalFormFields, form: FormApi<EditModalFormFields>) => {
      const inputBirthdate = input.birthdate as Date | string | null;

      const dirtyFields: { [key in keyof EditModalFormFields]?: boolean } =
        form.getState().dirtyFields;

      const { homeStores: homeStoresModified, ...memberDirtyFields } = dirtyFields;

      // check if any membership field was updated
      if (Object.keys(memberDirtyFields).length > 0) {
        let newBirthdate: string | null | undefined;

        if (inputBirthdate instanceof Date) {
          newBirthdate = formatDateTime(inputBirthdate, DateTimeFormat.DateDashed);
        } else {
          newBirthdate = inputBirthdate;
        }

        const update: MembershipUpdate = {
          // Some memberships may not have CustomerId.
          // Pass both, and the API will handle cases where one is missing.
          id: member.id,
          customerUuid: member.customerUuid,
          email: input.email,
          firstName: input.firstName,
          lastName: input.lastName,
          birthdate: newBirthdate,
          phone: formatPhoneNumberToE164(input.phone || ''),
          primaryAddress: {
            ...input.primaryAddress,
            state: input.primaryAddress?.state?.id || null,
          },
        };

        await updateMembership({
          variables: {
            input: update,
          },
        });
      }

      if (homeStoresModified && member.customerUuid) {
        await createOrUpdateHomeStores({
          variables: {
            input: {
              customerUuid: member.customerUuid,
              locationUuids: input.homeStores?.map((store) => store.uuid) || [],
            },
          },
        });
      }

      onHide();
    },
    [updateMembership, onHide, member.id, member.customerUuid, createOrUpdateHomeStores],
  );

  return (
    <EditModal
      member={member}
      locations={locations}
      show={show}
      onHide={onHide}
      onSubmit={onSubmit}
    />
  );
};

export default EditModalContainer;
